import classNames from "classnames";
import React, { ReactElement, useLayoutEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons"

import { 
  PlanIds,
  PlanInterval,
  // PlanIds, 
  PlanType, 
  ProductCategory, ProductType } from "../../reducers/models";
import  PlanCard, { PlanCardProps } from "./PlanCard";
import { useDispatch, useSelector } from "react-redux";
import currentSaleData from "../../selectors/currentSaleData";
import Log from "../../utils/Log";

import styles from "../../styles/plans/PlansOverview.module.scss"
import { calculateStripeTotal } from "../../utils/saleUtils";
import PLANS_JSON, { PLAN_MAP } from "../../constants/planMetadata";
import { fetchSale } from "../../actions/sale";
import classnames from "classnames";
//import Buildbox4PlanCard from "../../components/home/Buildbox4PlanCard";
import { getUserPlans } from "../../selectors/currentAccountSelector";

type DiscountInfo = bb.model.sale.IDiscountInfo
type SaleAsset = bb.model.sale.ISaleAsset
type CouponData = bb.model.sale.ICouponData
type Subscription = bb.model.ISubscription

export type Props = {
  selectedCategory: ProductCategory,
  categories: ProductCategory[],
  isLoading?:boolean,
  excludeFreeTier?: boolean,
  onProductSelected: (selected:ProductCategory)=>void,
  onPurchaseClicked: (planInfo:Subscription)=>void,
  homePage?: boolean,
}

const PRODUCT_CATEGORY_NAME = {
  [ProductCategory.Monthly]: "Buildbox Monthly",
  [ProductCategory.BB4]: "Buildbox 4",
  [ProductCategory.BB3]: "Buildbox 3",
  [ProductCategory.BB2]: "Buildbox Classic",
  [ProductCategory.BBBundle]: "Buildbox Bundle",
  [ProductCategory.Soundbox]: "Soundbox",
};

const PRODUCT_CATEGORY_BUTTON_TEXT = {
  [ProductCategory.Monthly]: "Monthly Plans",
  [ProductCategory.BB4]: "Buildbox 4",
  [ProductCategory.BB3]: "Buildbox 3",
  [ProductCategory.BB2]: "Buildbox Classic",
  [ProductCategory.BBBundle]: "Bundles",
  [ProductCategory.Soundbox]: "Soundbox"
};

const PRODUCT_SUBHEADING = {
  [ProductCategory.Monthly]: <>Same amazing software with no commitment!</>,
  [ProductCategory.BB2]: <>The Ultimate <b className={styles.versionName}>2D</b> Game Builder with Options at Every Level.</>,
  [ProductCategory.BB3]: <>The Ultimate <b className={styles.versionName}>2D+3D</b> Game Builder with Options at Every Level.</>,
  [ProductCategory.BB4]: <>The Ultimate <b className={styles.versionName}>3D</b> Game Builder with Enhanced AI.</>,
  [ProductCategory.BBBundle]: <>Featuring <b className={styles.versionName}>Pro</b> versions of Buildbox Classic, Buildbox 3, Buildbox 4 and Soundbox.</>,
  [ProductCategory.Soundbox]: <>The Ultimate <b className={styles.versionName}>Audio</b> Software for Game Developers.</>
}

const PRODUCT_SUBSUBHEADING = {
  [ProductCategory.Monthly]: undefined,
  [ProductCategory.BB2]: <a href="/product/bb2">Learn More</a>,
  [ProductCategory.BB3]: <a href="/product/bb3">Learn More</a>,
  [ProductCategory.BB4]: <a href="/product/bb4">Learn More</a>,
  [ProductCategory.Soundbox]: <a href="/product/soundbox">Learn More</a>,
  [ProductCategory.BBBundle]: undefined
}

function makePlanCardPropMapper(discountInfo?: DiscountInfo, saleAssets?:SaleAsset): (plan:Subscription)=>PlanCardProps {
  const bundleCostExtraText = saleAssets?.bundleCostExtraText
  return ({ id, planType, planInterval, planName, productType, productCategory, discountsAllowed, trialDays, annualCost, monthlyCost, preBundleCost:preBundleCostNum, featured, badgeText, features, nonGridFeatures, weeklyCost, lifetimeCost }: Subscription) => {

    let costNum: number = 0.00

    if(planInterval === PlanInterval.Weekly && typeof weeklyCost !== "undefined") {
      costNum = weeklyCost
    }
    else if(planInterval === PlanInterval.Monthly && typeof monthlyCost !== "undefined") {
      costNum = monthlyCost
    }
    else if(planInterval === PlanInterval.Yearly && typeof annualCost !== "undefined") {
      costNum = annualCost
    }
    else if(planInterval === PlanInterval.Lifetime && typeof lifetimeCost !== "undefined") {
      costNum = lifetimeCost
    }
    else {
      throw new Error(`Invalid cost for plan type: ${planType} interval: ${planInterval} annual: ${annualCost} monthly: ${monthlyCost}, weekly: ${weeklyCost} lifetime: ${lifetimeCost}`)
    }

    // calculate any sale discounts
    const productDiscountInfo = discountInfo?.[productType]
    let discountedCost: string | undefined
    if (planType !== PlanType.Free && discountsAllowed && productDiscountInfo !== undefined && productDiscountInfo[planType] !== undefined) {
      let discountedCostNum:number|undefined
      discountedCostNum = calculateStripeTotal(costNum, 1, productDiscountInfo[planType] as CouponData)
      if (discountedCostNum === undefined || isNaN(discountedCostNum)) {
        const msg = `Issue calculating discounted price for ${productType}-${planType}-${id}`
        Log.error(msg)
        Log.error(productDiscountInfo[planType])
        throw new Error(msg)
      }
      discountedCost = discountedCostNum + ""
    }
    let preBundleCost: string|undefined
    if (preBundleCostNum !== undefined) {
      preBundleCost = preBundleCostNum + ""
    }

    return {
      planId: id,
      productType,
      productCategory,
      planType,
      planInterval,
      planName,
      trialDays,
      cost: ""+costNum,
      discountedCost,
      preBundleCost,
      bundleCostExtraText,
      featured,
      badgeText,
      features,
      nonGridFeatures
    }
  }
}

// can use 'includeOnlyTheseTiers' array to filter, or just edit PLAN_MAP in planMetadata.ts (which is probably easier)
export function generatePlanCardProps(discountInfo?:DiscountInfo, saleAssets?:SaleAsset, includeOnlyTheseTiers?:PlanType[]):Record<ProductCategory, PlanCardProps[]> {
  const mapper = makePlanCardPropMapper(discountInfo, saleAssets)

  let bb4 = PLAN_MAP.BB4
  let bb3 = PLAN_MAP.BB3
  let bb2 = PLAN_MAP.BB2
  const monthly = PLAN_MAP.Monthly
  const bbBundle = PLAN_MAP.BBBundle
  const soundbox = PLAN_MAP.Soundbox
  // if (excludeFreeTier) {
  //   bb4 = bb4.filter(plan => plan.planType !== PlanType.Free)
  //   bb3 = bb3.filter(plan => plan.planType !== PlanType.Free)
  //   bb2 = bb2.filter(plan => plan.planType !== PlanType.Free)
  // }

  if(includeOnlyTheseTiers && includeOnlyTheseTiers.length > 0) {
    bb4 = bb4.filter(plan => includeOnlyTheseTiers.includes(plan.planType))
    bb3 = bb3.filter(plan => includeOnlyTheseTiers.includes(plan.planType))
    bb2 = bb2.filter(plan => includeOnlyTheseTiers.includes(plan.planType))
  }

  const result = {
    Monthly: monthly.map(mapper),
    BB4: bb4.map(mapper),
    BB3: bb3.map(mapper),
    BB2: bb2.map(mapper),
    BBBundle: bbBundle.map(mapper),
    Soundbox: soundbox.map(mapper),
  }

  return result
}

function renderProductButtons(selected:ProductCategory, categoryOrder:ProductCategory[], setSelectedHandler:(selected:ProductCategory)=>void):ReactElement {
  const buttons = categoryOrder
    .map((category) => {
      // if(category === ProductCategory.BB3){
      //   return(null)
      // }
      return (
        <button
          key={category}
          className={classNames(styles.button, {[styles.selected]: selected === category})}
          onClick={() => setSelectedHandler(category)}
        >
          {PRODUCT_CATEGORY_BUTTON_TEXT[category]}
        </button>
      )
    })
  return (
    <div className={styles.productButtons}>
      {buttons}
    </div>
  )
}

export function getPlanName(props:PlanCardProps): string {
  let prefix = ""
  if (props.planType === PlanType.Monthly) {
    if (props.productType === ProductType.BB2) {
      prefix = "Classic "
    }
    else if (props.productType === ProductType.BB3) {
      prefix = "BB3 "
    }
    else if (props.productType === ProductType.BB4) {
      prefix = "BB4 "
    }    
  }
  return `${prefix}${props.planName}`
}

export default function PlansOverview (
    { selectedCategory, categories, isLoading, excludeFreeTier=false, 
      onProductSelected, onPurchaseClicked, homePage = false }:Props
) : ReactElement {
  const dispatch = useDispatch()
  const saleData = useSelector(currentSaleData)
  const userPlans = useSelector(getUserPlans)


  useLayoutEffect(()=>{dispatch(fetchSale())}, [dispatch])

  // TODO: this function is reused in at least InlinePlans.tsx so the filter array should be a shared variable somewhere instead of copy-pasted
  const allCardProps = generatePlanCardProps(saleData.discountInfo, saleData.saleAssets)
  const cardProps:PlanCardProps[] = allCardProps[selectedCategory]

  // if there's only one plan, it might be nice to force it to be featured so the screen seems more alive
  // if (cardProps.length === 1) {
  //   cardProps[0].featured = true
  // }
  const atLeastOneSale = cardProps.findIndex(props => props.discountedCost !== undefined) !== -1
  return (
    <>
    {/* { selectedCategory ===ProductCategory.BB3 ? null
      :
      
            } */}
            <div className={classnames({[styles.plansOverviewContainer]: !homePage, [styles.plansOverviewContainerDarkTheme]: homePage})}>
        <div className={styles.splashTitle}>
          <h1>
            Choose the <b className={styles.versionName}>{PRODUCT_CATEGORY_NAME[selectedCategory]}</b> plan
            that's right for you!
          </h1>
          <h2>{PRODUCT_SUBHEADING[selectedCategory]} <span>{PRODUCT_SUBSUBHEADING[selectedCategory]}</span></h2>
        </div>
        {renderProductButtons(selectedCategory, categories, onProductSelected)}
        <div className={styles.plansOverview}>
          {cardProps.map(props => 
          {
            const lowerCaseProduct = props.productType.toLowerCase() as keyof bb.model.account.IPlans;

            // disable the button if the user is on the plan already
            let disabledButton = false;
            if(userPlans){
              const userPlanName = userPlans[lowerCaseProduct]?.plan?.name
              userPlanName && userPlanName === props.planName ? disabledButton = true : disabledButton = false
            }

            return (
              <PlanCard
                key={`${selectedCategory}${props.productType}${props.planType}`}
                {...props}
                planName={getPlanName(props)}
                useSaleLayout={atLeastOneSale}
                isLoading={isLoading}
                onPurchaseClicked={
                  (product:ProductType, planId:PlanIds) => {
                    onPurchaseClicked(PLANS_JSON[props.planId])
                  }
                }
                disableButton={disabledButton}
              />
            )}
          )}
        </div>
        <div className={styles.flexbreak}></div>
        <div className={classNames({[styles.planAd]:true, [styles.planAdHidden]:selectedCategory === ProductCategory.Monthly})}>
          { categories.includes(ProductCategory.Monthly) ? 
              <h4><FontAwesomeIcon className={styles.calendarIcon} icon={faCalendarAlt}/> <a className={styles.clickHereButton} href="/plans/monthly">Click Here</a> <span>for no commitment monthly plans starting at <span className={styles.money}>$9.99</span></span></h4> 
            : null }
        </div>
      </div>
  </>
  )
}
